import { useCallback, useRef, useState } from "react";

export interface AsyncAction<TArgs extends unknown[], TResult> {
  run: (...args: TArgs) => Promise<void>;
  isLoading: boolean;
  /** The message handed to the hook, set only after the action threw. */
  error: string | null;
  result: TResult | null;
}

/**
 * Runs an async action and tracks the three things every caller of one ends up tracking by
 * hand: whether it is in flight, what it returned, and what to tell the user when it threw.
 *
 * Each run clears the previous outcome, so a second attempt never shows the first one's
 * result next to the new error. The action and the message are held in refs, which keeps
 * `run` stable even though callers pass an inline function.
 */
export function useAsyncAction<TArgs extends unknown[], TResult>(
  action: (...args: TArgs) => Promise<TResult>,
  errorMessage: string,
): AsyncAction<TArgs, TResult> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>(null);
  const [result, setResult] = useState<TResult>(null);
  const actionRef = useRef(action);
  const errorMessageRef = useRef(errorMessage);

  actionRef.current = action;
  errorMessageRef.current = errorMessage;

  const run = useCallback(async (...args: TArgs) => {
    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      setResult(await actionRef.current(...args));
    } catch (caught) {
      // The caught value carries the detail; the message is only what the user reads.
      console.error(errorMessageRef.current, caught);
      setError(errorMessageRef.current);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { run, isLoading, error, result };
}
